"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, PlusCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/app/_module/components/ui/dialog";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { Textarea } from "@/app/_module/components/ui/textarea";
import { useToast } from "@/app/_module/components/ui/use-toast";
import { FORUM_USER_ROOMS_ENABLED } from "@/app/_module/utils/featureFlags";
import { forumService, forumPath } from "./ForumService";
import { CategoryIconTile } from "./categoryIcons";
import { useForumAccess } from "../hooks/useForumAccess";

// "Start a community" — user-created subs. Server enforces the guardrails
// (50+ Rep or admin, one per week); this dialog just makes it feel premium.
export function CreateSubDialog() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { requireWrite, isModerator } = useForumAccess();
  const { toast } = useToast();
  const router = useRouter();
  const queryClient = useQueryClient();

  // Rooms are curated by InsuraCentral for now (locked 2026-09-02). Admins
  // still get the control; agents see nothing until the flag opens it.
  if (!isModerator && !FORUM_USER_ROOMS_ENABLED) return null;

  const previewSlug = name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/[\s-]+/g, "-");

  const submit = async () => {
    if (name.trim().length < 3) return;
    setSubmitting(true);
    try {
      const res = await forumService.createCategory({ name: name.trim(), description: description.trim() || undefined });
      queryClient.invalidateQueries({ queryKey: ["forum", "categories"] });
      toast({ title: `c/${res.slug} is live`, description: "Your community is open — make the first post.", duration: 4000 });
      setOpen(false);
      setName("");
      setDescription("");
      router.push(forumPath(`/c/${res.slug}`));
    } catch (e: any) {
      toast({ title: "Couldn't create community", description: e.message, variant: "destructive", duration: 6000 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            requireWrite(() => setOpen(true));
          }}
          className="mt-1 w-full flex items-center gap-2.5 rounded-lg border border-dashed border-border px-2 py-1.5 text-sm text-muted-foreground transition-all hover:border-primary/40 hover:text-primary hover:bg-primary/5"
        >
          <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-dashed border-current/40">
            <PlusCircle className="h-3.5 w-3.5" />
          </span>
          Start a community
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Start a community</DialogTitle>
          <DialogDescription>
            Your own room in the agent community. Needs 50+ Rep — earned by posting and contributing. One new
            community per week.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 40))}
              placeholder="Community name — e.g. IUL & Cash Value"
              className="text-sm font-medium"
            />
            {previewSlug && (
              <div className="mt-1.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                <CategoryIconTile slug={previewSlug} size="sm" />
                <span>
                  forum/c/<span className="font-semibold text-foreground/80">{previewSlug}</span>
                </span>
              </div>
            )}
          </div>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, 200))}
            placeholder="One line on what this room is for (optional)"
            rows={2}
            className="text-sm"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={submitting || name.trim().length < 3}>
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
            Create community
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
